import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  useCallback,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { truncationPredicates, useTooltip } from '__components/Kizen/Tooltip';
import {
  StyledDashedUnderline,
  StyledMenu,
  StyledUserDescription,
} from './styles';
import { Searchable } from './Searchable';
import { KizenTypography } from '__app/typography';
import {
  MarkdownEditor,
  type MarkdownHandle,
} from 'components/WYSIWYG/MarkdownEditor';
import { getRawTextValue } from 'components/WYSIWYG/MarkdownEditor/helpers';
import { MAX_DESCRIPTION_LENGTH } from 'pages/AutomationEngine/dialogs/UserDescription/userDescriptionContext';

const overflowXOrY = (el: HTMLElement) =>
  truncationPredicates.x(el) || truncationPredicates.y(el);

const EDITOR_WIDTH = 300;

export const EditableDescription: FC<{
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  userDescription: string | null;
  stepId: string;
  editable: boolean;
  hovered: boolean;
  onChange: (userDescription: string) => void;
}> = ({
  userDescription,
  stepId,
  hovered,
  editable,
  onChange,
  isOpen,
  setIsOpen,
}) => {
  const [tooltipProps, tooltip] = useTooltip({ html: true }, overflowXOrY);

  const editorRef = useRef<MarkdownHandle | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<Element | null>(null);
  const lastRectRef = useRef<DOMRect | null>(null);
  const lastPopupHeightRef = useRef<number | null>(null);

  const [popupPosition, setPopupPosition] = useState<{
    top: string | number;
    bottom: string | number;
    left: string | number;
    right: string | number;
  } | null>(null);

  const handleOpen = () => {
    setIsOpen(editable);
  };

  const handleSave = useCallback(
    (value: string) => {
      setIsOpen(false);
      onChange(value);
    },
    [onChange, setIsOpen]
  );

  useLayoutEffect(() => {
    if (isOpen) {
      let frameId: number | null = null;
      const target = targetRef.current;
      const container = document.querySelector('.FlowContainer');
      containerRef.current = container;

      const syncPosition = () => {
        if (target && container) {
          const lastRect = lastRectRef.current;
          const rect = target.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const popupHeight = popupRef.current?.clientHeight ?? 0;
          if (
            !lastRect ||
            lastRect.top !== rect.top ||
            lastRect.left !== rect.left ||
            lastPopupHeightRef.current !== popupHeight
          ) {
            lastRectRef.current = rect;
            lastPopupHeightRef.current = popupHeight;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < popupHeight;
            setPopupPosition({
              top: openAbove
                ? 'auto'
                : rect.bottom - containerRect.top - rect.height,
              bottom: openAbove
                ? window.innerHeight - rect.top - rect.height - 1
                : 'auto',
              left: rect.left - containerRect.left,
              right: 'auto',
            });
          }
        }
        frameId = requestAnimationFrame(syncPosition);
      };

      syncPosition();

      let pressStartedInside = false;

      const trackPressStart = (event: Event) => {
        pressStartedInside = !!popupRef.current?.contains(event.target as Node);
      };

      const closeOutside = (event: Event) => {
        if (pressStartedInside) {
          pressStartedInside = false;
          return;
        }
        if (!popupRef.current?.contains(event.target as Node)) {
          getRawTextValue(editorRef.current!.editor, false).then((value) => {
            handleSave(value);
            setIsOpen(false);
          });
        }
      };

      const closeOnEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };

      document.addEventListener('pointerdown', trackPressStart, true);
      document.addEventListener('click', closeOutside, true);
      document.addEventListener('keydown', closeOnEscape);

      return () => {
        document.removeEventListener('pointerdown', trackPressStart, true);
        document.removeEventListener('click', closeOutside, true);
        document.removeEventListener('keydown', closeOnEscape);
        if (frameId) {
          cancelAnimationFrame(frameId);
        }
        lastRectRef.current = null;
        lastPopupHeightRef.current = null;
      };
    }
  }, [isOpen, setIsOpen, handleSave]);

  useEffect(() => {
    return () => setIsOpen(false);
  }, [setIsOpen]);

  const initialValue = useMemo(
    () =>
      (userDescription ?? '')
        .replace(/\s*[\r\n]+\s*/g, ' ')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;'),
    [userDescription]
  );

  return (
    <>
      {userDescription ? (
        <StyledDashedUnderline hovered={editable && hovered && !isOpen}>
          <StyledUserDescription
            {...tooltipProps}
            data-qa="user-description"
            onClick={handleOpen}
            ref={targetRef}
          >
            <Searchable stepId={stepId} enabled>
              <KizenTypography as="span">{userDescription}</KizenTypography>
            </Searchable>
          </StyledUserDescription>
        </StyledDashedUnderline>
      ) : (
        <div ref={targetRef} style={{ marginTop: -10 }} />
      )}
      {tooltip}
      {isOpen && popupPosition && containerRef.current && lastRectRef.current
        ? createPortal(
            <StyledMenu ref={popupRef} style={popupPosition}>
              <MarkdownEditor
                isMarkdown={false}
                initialValue={initialValue}
                autofocus="end"
                onSave={handleSave}
                editorRef={editorRef}
                readonly={!editable}
                initialWidth={EDITOR_WIDTH}
                maxLength={MAX_DESCRIPTION_LENGTH}
                singleLine
              />
            </StyledMenu>,
            containerRef.current
          )
        : null}
    </>
  );
};
